import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu,
  X,
  Inbox,
  Send,
  FileText,
  Trash2,
  Archive,
  Star,
  RefreshCw,
  Plus,
  Settings,
  ChevronLeft,
} from 'lucide-react';

import { MailList, ThreadDisplay } from './inbox';
import { EmailComposer } from './composer';
import { useOutlookThreads, useOutlookEmail, useOutlookComposer } from './hooks';

/**
 * EmailInbox - Complete email inbox component
 * Combines MailList, ThreadDisplay, and EmailComposer
 */
const EmailInbox = ({
  connectionId = null,
  onConnectionRequired,
  height = '100%',
  showSidebar = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [composeOpen, setComposeOpen] = useState(false);

  // Hooks
  const {
    threads,
    selectedThread,
    threadMessages,
    loading,
    loadingThread,
    error,
    hasMore,
    fetchThreads,
    loadMore,
    refresh,
    selectThread,
    closeThread,
  } = useOutlookThreads({
    folderId: currentFolder,
    connectionId,
    autoFetch: true,
  });

  const {
    sendEmail,
    markAsRead,
    deleteMessage,
    moveMessage,
  } = useOutlookEmail();

  // Folders
  const folders = [
    { id: 'inbox', label: 'Inbox', icon: Inbox, count: 0 },
    { id: 'sentitems', label: 'Sent', icon: Send, count: 0 },
    { id: 'drafts', label: 'Drafts', icon: FileText, count: 0 },
    { id: 'archive', label: 'Archive', icon: Archive, count: 0 },
    { id: 'deleteditems', label: 'Trash', icon: Trash2, count: 0 },
  ];

  // Handle folder change
  const handleFolderChange = useCallback((folderId) => {
    setCurrentFolder(folderId);
    closeThread();
    if (isMobile) setSidebarOpen(false);
  }, [closeThread, isMobile]);

  // Handle thread selection
  const handleSelectThread = useCallback((threadId) => {
    selectThread(threadId);
  }, [selectThread]);

  // Handle compose
  const handleOpenCompose = useCallback(() => {
    setComposeOpen(true);
  }, []);

  const handleCloseCompose = useCallback(() => {
    setComposeOpen(false);
  }, []);

  // Handle send email
  const handleSendEmail = useCallback(async (data) => {
    try {
      await sendEmail({
        to: data.to,
        cc: data.cc,
        bcc: data.bcc,
        subject: data.subject,
        message: data.message,
        attachments: data.attachments,
        connectionId,
      });
    } catch (err) {
      // Log error but don't throw - email might have been sent despite error response
      console.error('Send email error (email may still have been sent):', err);
    } finally {
      // Always close composer and refresh - email is likely sent even if server returned error
      handleCloseCompose();
      refresh();
    }
  }, [sendEmail, connectionId, handleCloseCompose, refresh]);

  // Handle send reply
  const handleSendReply = useCallback(async (data) => {
    try {
      await sendEmail({
        to: data.to,
        cc: data.cc,
        bcc: data.bcc,
        subject: data.subject,
        message: data.message,
        attachments: data.attachments,
        headers: data.headers,
        connectionId,
      });
    } catch (err) {
      console.error('Failed to send reply:', err);
      // Don't throw - let the composer close anyway
    } finally {
      // Always refresh after attempt
      refresh();
    }
  }, [sendEmail, connectionId, refresh]);

  // Handle delete
  const handleDelete = useCallback(async (messageId) => {
    try {
      await deleteMessage(messageId);
      closeThread();
      refresh();
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  }, [deleteMessage, closeThread, refresh]);

  // Handle archive
  const handleArchive = useCallback(async (messageId) => {
    try {
      await moveMessage(messageId, 'archive');
      closeThread();
      refresh();
    } catch (err) {
      console.error('Failed to archive message:', err);
    }
  }, [moveMessage, closeThread, refresh]);

  // Handle mark as read
  const handleMarkRead = useCallback(async (messageId, currentUnread) => {
    try {
      await markAsRead(messageId, currentUnread);
      refresh();
    } catch (err) {
      console.error('Failed to mark message:', err);
    }
  }, [markAsRead, refresh]);

  // Get user email from localStorage
  const userEmail = localStorage.getItem('userEmail') || '';

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ width: 240, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<Plus size={18} />}
          onClick={handleOpenCompose}
        >
          Compose
        </Button>
      </Box>
      
      <Divider />
      
      <List sx={{ flex: 1 }}>
        {folders.map((folder) => {
          const Icon = folder.icon;
          return (
            <ListItem key={folder.id} disablePadding>
              <ListItemButton
                selected={currentFolder === folder.id}
                onClick={() => handleFolderChange(folder.id)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon size={20} />
                </ListItemIcon>
                <ListItemText primary={folder.label} />
                {folder.count > 0 && (
                  <Badge badgeContent={folder.count} color="primary" />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height, bgcolor: 'background.default' }}>
      {/* Sidebar */}
      {showSidebar && (
        isMobile ? (
          <Drawer
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            sx={{ '& .MuiDrawer-paper': { width: 240 } }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <Paper
            sx={{
              width: 240,
              borderRight: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
            }}
          >
            {sidebarContent}
          </Paper>
        )
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && showSidebar && (
              <IconButton onClick={() => setSidebarOpen(true)}>
                <Menu size={20} />
              </IconButton>
            )}
            <Typography variant="h6">
              {folders.find(f => f.id === currentFolder)?.label || 'Inbox'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </IconButton>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Mail List */}
          <Box
            sx={{
              width: selectedThread && !isMobile ? 350 : '100%',
              borderRight: selectedThread ? '1px solid' : 'none',
              borderColor: 'divider',
              display: selectedThread && isMobile ? 'none' : 'block',
              overflow: 'hidden',
            }}
          >
            <MailList
              threads={threads}
              selectedId={selectedThread}
              onSelect={handleSelectThread}
              loading={loading && threads.length === 0}
              loadingMore={loading && threads.length > 0}
              onLoadMore={loadMore}
              hasMore={hasMore}
              emptyMessage={`No emails in ${currentFolder}`}
            />
          </Box>

          {/* Thread Display */}
          {selectedThread && (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <ThreadDisplay
                thread={threads.find(t => t.id === selectedThread)}
                messages={threadMessages}
                loading={loadingThread}
                onClose={isMobile ? closeThread : undefined}
                onSendReply={handleSendReply}
                onDelete={handleDelete}
                onArchive={handleArchive}
                onMarkRead={handleMarkRead}
                userEmail={userEmail}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Compose Dialog */}
      <Drawer
        anchor="right"
        open={composeOpen}
        onClose={handleCloseCompose}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 600,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">New Message</Typography>
            <IconButton onClick={handleCloseCompose}>
              <X size={20} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            <EmailComposer
              onSend={handleSendEmail}
              onClose={handleCloseCompose}
              mode="compose"
              compact
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

EmailInbox.propTypes = {
  connectionId: PropTypes.string,
  onConnectionRequired: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSidebar: PropTypes.bool,
};

export default EmailInbox;
